import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Button } from "@zillow/constellation-10";
import { IconArrowRightOutline } from "@zillow/constellation-icons/react/outline/icon-arrow-right-outline";
import { useYieldCallback } from "@zillow/yield-callback";
import PropTypes from "prop-types";
import React from "react";
import { isBrowser } from "../../utils/utilities.esm.js";
import { ListItemWithProgress, ImageWithProgress } from "./ProgressTracker.esm.js";
const MediaStepper = ({
  id,
  animationTime = 6,
  buttonText,
  children,
  collapseOthers = false,
  onButtonClick,
  reverse,
  onStepClick,
  mediaSizing = "v8"
}) => {
  const [skipAnimation, setSkipAnimation] = React.useState(false);
  const [activeNumber, setActiveNumber] = React.useState(0);
  const intervalId = React.useRef(void 0);
  const [hashNavigated, setHashNavigated] = React.useState(false);
  const scrollToIdRef = React.useRef(null);
  const clearCurrentInterval = React.useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = void 0;
    }
  }, []);
  const onPropClick = useYieldCallback((propNumber, div) => {
    setSkipAnimation(true);
    setActiveNumber(propNumber);
    clearCurrentInterval();
    onStepClick == null ? void 0 : onStepClick(div);
  }, [clearCurrentInterval, onStepClick]);
  const items = React.useMemo(() => React.Children.toArray(children).filter((child) => child.type === MediaStepper.Item), [children]);
  const handleHashNavigation = React.useCallback(() => {
    if (!isBrowser) {
      return;
    }
    const hash = window.location.hash;
    if (!hash) {
      return;
    }
    const targetId = hash.substring(1);
    const itemIndex = items.findIndex((item) => item.props.id && item.props.id === targetId);
    if (itemIndex === -1) {
      return;
    }
    setActiveNumber(itemIndex);
    setSkipAnimation(true);
    setHashNavigated(true);
    scrollToIdRef.current = targetId;
    clearCurrentInterval();
  }, [items, clearCurrentInterval]);
  React.useEffect(() => {
    handleHashNavigation();
  }, [handleHashNavigation]);
  React.useLayoutEffect(() => {
    if (!scrollToIdRef.current) {
      return;
    }
    const targetId = scrollToIdRef.current;
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      const itemIndex = items.findIndex((item) => item.props.id === targetId);
      if (itemIndex === -1) {
        return;
      }
      setActiveNumber(itemIndex);
    }
    scrollToIdRef.current = null;
  }, [items]);
  React.useEffect(() => {
    if (!isBrowser) {
      return;
    }
    window.addEventListener("hashchange", handleHashNavigation);
    return () => {
      window.removeEventListener("hashchange", handleHashNavigation);
    };
  }, [handleHashNavigation]);
  React.useEffect(() => {
    if (!animationTime || animationTime < 0 || hashNavigated) {
      return;
    }
    clearCurrentInterval();
    intervalId.current = setInterval(() => {
      setActiveNumber((prevActiveNumber) => (prevActiveNumber + 1) % items.length);
    }, 1e3 * animationTime);
    return clearCurrentInterval;
  }, [animationTime, hashNavigated, items.length, clearCurrentInterval]);
  const onButtonClickYielded = useYieldCallback(() => {
    onButtonClick == null ? void 0 : onButtonClick();
  }, [onButtonClick]);
  const renderListItems = React.useCallback((item, index) => {
    const active = index === activeNumber;
    return /* @__PURE__ */ jsx(React.Fragment, { children: /* @__PURE__ */ jsx(ListItemWithProgress, { heading: item.props.heading, active, animationTime, skipAnimation, onClick: (div) => {
      onPropClick(index, div);
    }, collapseOthers, id: item.props.id, children: item.props.children }) }, index);
  }, [activeNumber, animationTime, skipAnimation, onPropClick, collapseOthers]);
  const renderImageItems = React.useCallback((item, index) => /* @__PURE__ */ jsx(ImageWithProgress, { active: activeNumber === index, media: item.props.image, reverse, mediaSizing }, index), [activeNumber, mediaSizing, reverse]);
  return /* @__PURE__ */ jsx("div", { id, children: /* @__PURE__ */ jsxs(Box, { css: {
    gap: "{spacing.400}",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    lg: {
      gap: "{spacing.600}",
      flexDirection: "row",
      alignItems: "stretch"
    }
  }, children: [
    /* @__PURE__ */ jsx(Box, { css: {
      ...reverse && {
        order: 1
      },
      boxSizing: "border-box",
      width: "100%",
      minWidth: "0",
      maxWidth: "100%",
      marginRight: "0",
      marginLeft: "0",
      paddingRight: "0",
      paddingLeft: "0",
      lg: {
        flex: "1 1 0%",
        minWidth: "0",
        minHeight: "0",
        display: "flex",
        flexDirection: "column"
      }
    }, children: /* @__PURE__ */ jsxs(Box, { css: {
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: "{spacing.800}",
      marginRight: "0",
      marginLeft: "0",
      minWidth: "0",
      maxWidth: "100%",
      width: "auto",
      lg: {
        flex: "1",
        minHeight: "0",
        height: "100%",
        // Always indent from the outer edge (same as original): right margin in both
        // layouts so reversed mode does not put 15% between image and text.
        marginRight: "15%",
        marginLeft: "0"
      }
    }, children: [
      items.map(renderListItems),
      !!buttonText && /* @__PURE__ */ jsxs(Button, { type: "button", tone: "brand", emphasis: "filled", onClick: onButtonClickYielded, css: {
        marginTop: "{spacing.800} !important",
        alignSelf: "flex-start",
        display: "inline-flex",
        alignItems: "center",
        gap: "{spacing.200}"
      }, children: [
        buttonText,
        /* @__PURE__ */ jsx(IconArrowRightOutline, { "aria-hidden": true })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(Box, { "data-testid": "overlapping-stack", css: {
      display: "none",
      minWidth: "0",
      width: "100%",
      maxWidth: "100%",
      lg: {
        flex: "1 1 0%",
        minHeight: "0",
        display: "grid",
        // 1fr uses this column's flex share; max-content was shrinking to ~intrinsic img width,
        // so Subheading 1 never benefited from a higher maxWidth cap on ImageWithProgress.
        gridTemplateColumns: "minmax(0, 1fr)",
        gridTemplateRows: "auto",
        alignContent: "start",
        width: "100%",
        maxWidth: "100%",
        justifyItems: "stretch",
        "& > *": {
          gridArea: "1 / 1",
          minWidth: "0",
          maxWidth: "100%",
          // Stretch so each layer spans the column; alignment stays via ImageWithProgress flex.
          justifySelf: "stretch",
          width: "100%",
          alignSelf: "start",
          height: "fit-content"
        }
      }
    }, children: items.map(renderImageItems) })
  ] }) });
};
MediaStepper.Item = () => null;
MediaStepper.Item.displayName = "MediaStepper.Item";
MediaStepper.Item.propTypes = {
  children: PropTypes.node,
  heading: PropTypes.string.isRequired,
  image: PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired,
  id: PropTypes.string
};
MediaStepper.propTypes = {
  id: PropTypes.string,
  animationTime: PropTypes.number,
  buttonText: PropTypes.string,
  children: PropTypes.node.isRequired,
  collapseOthers: PropTypes.bool,
  onButtonClick: PropTypes.func,
  reverse: PropTypes.bool,
  onStepClick: PropTypes.func,
  mediaSizing: PropTypes.oneOf(["v8", "fullColumn"])
};
export {
  MediaStepper as default
};
